/**
 * Pure shipment save contract (no Vue / Pinia).
 * Stop wipe rules mirror gpx-device-api `docs/shipment-stops-sync-api.md`
 * (`SyncShipmentStopsAndRoutes`). POST vs PUT is from `persistedStopId`, not a flag.
 */
import type { RoutePlan } from '@gpx-spa/geo-domain';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { shipmentFormStopsToDeviceApiStops } from '@/components/shipment/form/utils/shipment-form-payload';
import { stopPayloadInvalidatesServerRoutesFromPersistedSnapshot } from '@/components/shipment/form/utils/stop-payload-invalidates-server-routes';
import { hasPersistableRoutePlan } from '@/components/shipment/form/utils/shipment-form-route-composer-snapshot';
import type { ShipmentFormSavePolicy } from '@/composables/shipment-form-save-policy';

export type StopPayload = Record<string, unknown>;

export type StopWrite =
  | { method: 'skip' }
  | { method: 'post'; stops: StopPayload[] }
  | { method: 'put'; stops: StopPayload[] };

export type RouteWrite =
  | { mode: 'skip' }
  | { mode: 'upsert' }
  | { mode: 'recreate'; serverClearsRoutes: true };

export type ShipmentSavePlan = {
  metadata: 'skip' | 'patch';
  devices: 'skip' | 'post';
  stops: StopWrite;
  routes: RouteWrite;
  publish: boolean;
  /** True when PUT/POST stops will wipe shipment_routes on device-api. */
  stopsInvalidatesRoutes: boolean;
};

export type ShipmentSavePlanInput = {
  policy: ShipmentFormSavePolicy;
  metadataChanged: boolean;
  stopsChangedSincePersisted: boolean;
  persistedStopsPayloadSnapshot: string | null;
  stops: ShipmentFormStopRow[];
  deviceIdsChanged: boolean;
  routeComposerChanged: boolean;
  routePlan: RoutePlan | null;
  includePublish: boolean;
  /**
   * Create publish: radius-only (or other non-wipe) stop edits still upsert routes.
   * Edit does not set this.
   */
  createPublishRouteUpsert?: boolean;
};

export function hasPersistedStopIds(
  rows: readonly ShipmentFormStopRow[],
): boolean {
  return rows.some(
    (row) => typeof row.persistedStopId === 'number' && row.persistedStopId > 0,
  );
}

/**
 * POST vs PUT from whether any stop already has a server id — not a hydration flag.
 * POST never includes `id` (device-api StoreShipmentStopsRequest prohibits it).
 */
export function resolveStopWrite(input: {
  canEditRoute: boolean;
  stopsDirty: boolean;
  stops: ShipmentFormStopRow[];
}): StopWrite {
  if (!input.canEditRoute || !input.stopsDirty) {
    return { method: 'skip' };
  }
  if (hasPersistedStopIds(input.stops)) {
    return {
      method: 'put',
      stops: shipmentFormStopsToDeviceApiStops(input.stops, {
        includeIds: true,
      }),
    };
  }
  return {
    method: 'post',
    stops: shipmentFormStopsToDeviceApiStops(input.stops, {
      includeIds: false,
    }),
  };
}

function resolveRouteWrite(input: {
  canEditRoute: boolean;
  stopsDirty: boolean;
  stopsInvalidatesRoutes: boolean;
  routeComposerChanged: boolean;
  routePlan: RoutePlan | null;
  createPublishRouteUpsert: boolean;
}): RouteWrite {
  if (!input.canEditRoute) return { mode: 'skip' };
  const hasPlan = hasPersistableRoutePlan(input.routePlan);
  if (input.stopsInvalidatesRoutes && hasPlan) {
    return { mode: 'recreate', serverClearsRoutes: true };
  }
  if (!hasPlan) return { mode: 'skip' };
  if (input.routeComposerChanged) return { mode: 'upsert' };
  if (
    input.createPublishRouteUpsert &&
    input.stopsDirty &&
    !input.stopsInvalidatesRoutes
  ) {
    return { mode: 'upsert' };
  }
  return { mode: 'skip' };
}

export function buildShipmentSavePlan(
  input: ShipmentSavePlanInput,
): ShipmentSavePlan {
  const metadata: ShipmentSavePlan['metadata'] =
    input.policy.canEditMetadata && input.metadataChanged ? 'patch' : 'skip';

  const stopsDirty =
    input.policy.canEditRoute && input.stopsChangedSincePersisted;

  let stopsInvalidatesRoutes = false;
  if (stopsDirty) {
    stopsInvalidatesRoutes =
      stopPayloadInvalidatesServerRoutesFromPersistedSnapshot(
        input.persistedStopsPayloadSnapshot,
        input.stops,
      );
  }

  const stops = resolveStopWrite({
    canEditRoute: input.policy.canEditRoute,
    stopsDirty,
    stops: input.stops,
  });

  const routes = resolveRouteWrite({
    canEditRoute: input.policy.canEditRoute,
    stopsDirty,
    stopsInvalidatesRoutes,
    routeComposerChanged: input.routeComposerChanged,
    routePlan: input.routePlan,
    createPublishRouteUpsert: input.createPublishRouteUpsert === true,
  });

  const devices: ShipmentSavePlan['devices'] =
    input.policy.canEditTracker && input.deviceIdsChanged ? 'post' : 'skip';

  return {
    metadata,
    devices,
    stops,
    routes,
    publish: input.includePublish,
    stopsInvalidatesRoutes,
  };
}

export function savePlanHasWork(plan: ShipmentSavePlan): boolean {
  return (
    plan.metadata !== 'skip' ||
    plan.stops.method !== 'skip' ||
    plan.routes.mode !== 'skip' ||
    plan.devices !== 'skip'
  );
}
